import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApiHeaders } from "@/hooks/use-api-headers";
import type { WasteEvent } from "@/hooks/use-waste-events";
import { useTranslation } from "@/i18n/use-translation";
import { apiError } from "@/lib/api-error";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

// Manual waste log (POS2-61): always L4, source=manual, confirmed on create.
export type CreateWasteInput = {
  kitchen_id: string;
  item_weight: number;
  logged_at: string;
  waste_type: string;
  classification_level?: number;
  category_id?: string | null;
  subcategory_id?: string | null;
  inventory_item_id?: string | null;
  recipe_id?: string | null;
  device_serial?: string | null;
  ai_reasoning?: string | null;
  source?: string;
  status?: string;
  is_confirmed?: boolean;
};

/**
 * Create a manual waste event (POS2-61) — no photo, human-identified L4.
 * Invalidates the feed so the new card appears.
 */
export function useCreateWasteEvent() {
  const { t } = useTranslation();
  const headers = useApiHeaders();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: CreateWasteInput): Promise<WasteEvent> => {
      const res = await fetch(`${API_URL}/api/v1/waste/events`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        throw await apiError(t, res);
      }
      return ((await res.json()) as { data: WasteEvent }).data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["waste-events"] });
    },
  });
}

// Edit a manual waste log (POS2-61). All fields optional — only changed ones sent.
export type UpdateWasteInput = {
  eventId: string;
  item_weight?: number;
  logged_at?: string;
  waste_type?: string;
  classification_level?: number;
  category_id?: string | null;
  subcategory_id?: string | null;
  inventory_item_id?: string | null;
  recipe_id?: string | null;
  device_serial?: string | null;
  ai_reasoning?: string | null;
};

/**
 * Edit an existing waste event (POS2-61) via PATCH. Used by the Log Waste
 * dialog in edit mode; recompute of cost/service_time happens server-side.
 */
export function useUpdateWasteEvent() {
  const { t } = useTranslation();
  const headers = useApiHeaders();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ eventId, ...body }: UpdateWasteInput): Promise<WasteEvent> => {
      const res = await fetch(`${API_URL}/api/v1/waste/events/${eventId}`, {
        method: "PATCH",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        throw await apiError(t, res);
      }
      return ((await res.json()) as { data: WasteEvent }).data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["waste-events"] });
    },
  });
}

/**
 * Run the three-pass AI classifier on a single unprocessed event (POS2-253).
 * The backend persists the classification onto the event; we return the
 * updated row and invalidate the feed so the card re-renders with its tier,
 * label, and cost. One event per call — bulk "Analyse All" is POS2-698.
 */
export function useAnalyseWasteEvent() {
  const { t } = useTranslation();
  const headers = useApiHeaders();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (eventId: string): Promise<WasteEvent> => {
      const res = await fetch(`${API_URL}/api/v1/waste/events/${eventId}/analyse`, {
        method: "POST",
        headers,
      });
      if (!res.ok) {
        throw await apiError(t, res);
      }
      const body = (await res.json()) as { data: WasteEvent };
      return body.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["waste-events"] });
    },
  });
}

// Cost subobject returned by the confirm/relabel endpoints (POS2-627, spec §7.3).
export type ConfirmedCost = {
  estimate: number;
  method: string;
  currency: string;
  recalculated: boolean;
};

/**
 * Confirm an AI classification (POS2-62, spec §10.4). Stamps the event
 * confirmed and recomputes its cost server-side. Invalidates the feed so the
 * card re-renders in its settled state with the refreshed cost.
 */
export function useConfirmWasteEvent() {
  const { t } = useTranslation();
  const headers = useApiHeaders();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (eventId: string): Promise<ConfirmedCost | null> => {
      const res = await fetch(`${API_URL}/api/v1/waste/events/${eventId}/confirm`, {
        method: "POST",
        headers,
      });
      if (!res.ok) {
        throw await apiError(t, res);
      }
      const body = (await res.json()) as { data: { cost: ConfirmedCost | null } };
      return body.data.cost;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["waste-events"] });
    },
  });
}

// Explicit null clears a field server-side (correcting to a shallower level
// must drop the stale deeper label); omitting it keeps the existing value
// (adopt-suggestion sends only inventory_item_id).
export type RelabelInput = {
  eventId: string;
  category_id?: string | null;
  subcategory_id?: string | null;
  inventory_item_id?: string | null;
};

/**
 * Relabel (correct) a classification (POS2-62, spec §10.4/§10.5). Applies the
 * new label, confirms, and recomputes cost server-side. Used for the L3
 * "adopt the suggested item" one-tap and (slice 2) the manual picker.
 */
export function useRelabelWasteEvent() {
  const { t } = useTranslation();
  const headers = useApiHeaders();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ eventId, ...body }: RelabelInput): Promise<ConfirmedCost | null> => {
      const res = await fetch(`${API_URL}/api/v1/waste/events/${eventId}/relabel`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        throw await apiError(t, res);
      }
      const json = (await res.json()) as { data: { cost: ConfirmedCost | null } };
      return json.data.cost;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["waste-events"] });
    },
  });
}
